import Cookies from "js-cookie";
import { AuthHandler } from "./authHandler";
import { refreshCognitoTokens, extractCognitoUsername } from "../utils/cognitoRefresh";

type ResponseType = "json" | "blob" | "text" | "none";

class AwsApiClient {
  private host: string;
  private env: string;

  constructor() {
    //console.log(process.env);
    this.host = process.env.REACT_APP_BACKEND!;
    this.env = process.env.REACT_APP_ENV!;
  }



  private getCookie(name: string): string | null {
    const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
    return match ? decodeURIComponent(match[2]) : null;
  }

  private async makeRequest(
    method: string,
    path: string,
    body: any = null,
    responseType: ResponseType = "json",
    contentType: string = "application/json"
  ): Promise<any> {
    const url = `${this.host}${path}`;
    const urlObject = new URL(url);
    const headers: any = {
      Host: urlObject.host,
    };

    const authToken = this.getCookie("id_token");

    if (authToken) {
      headers["Authorization"] = `Bearer ${authToken}`;
    }

    if (contentType !== "multipart/form-data") {
      headers["Content-Type"] = contentType;
    }

    let response: Response | null = null;

    if (method === "GET" || method === "DELETE") {
      //response = await this.aws.fetch(url, {
      response = await fetch(url, {
        method,
        headers,
      });
    } else if (method === "POST") {
      let requestBody: any;
      if (contentType === "application/json") {
        requestBody = body ? JSON.stringify(body) : null;
      } else if (contentType === "application/x-www-form-urlencoded") {
        requestBody = body ? new URLSearchParams(body).toString() : null;
      } else {
        requestBody = body;
      }

      //response = await this.aws.fetch(url, {
      response = await fetch(url, {
        method,
        headers,
        body: requestBody,
      });
    }

    if (!response || !response.ok) {
      if (response?.status === 401) {
        // Attempt a single token refresh + retry before logging out
        const refreshed = await this.tryRefreshAndRetry(method, path, body, responseType, contentType);
        if (refreshed !== null) {
          return refreshed;
        }
        AuthHandler.logout();
        throw new Error('Session expired');
      }
      const error = response ? await response.json().catch(() => null) : null;
      const errorMessage = error?.error || error?.message || 'Request failed';
      throw new Error(errorMessage);
    }

    switch (responseType) {
      case "blob":
        return await response.blob();
      case "text":
        return await response.text();
      case "none":
        return response;
      default:
        return await response.json();
    }
  }

  private async makeExternalRequest(
    method: string,
    path: string,
    body: any = null,
    responseType: ResponseType = "json",
    contentType: string = "application/json"
  ): Promise<any> {
    const url = `${this.host}${path}`;
    const urlObject = new URL(url);
    const headers: any = {
      Host: urlObject.host,
    };

    headers["Content-Type"] = contentType;
    let response: Response | null = null;

    if (method === "GET" || method === "DELETE") {
      response = await fetch(url, {
        method,
        headers,
      });
    } else if (method === "POST") {
      let requestBody: any;
      if (contentType === "application/json") {
        requestBody = body ? JSON.stringify(body) : null;
      } else if (contentType === "application/x-www-form-urlencoded") {
        requestBody = body ? new URLSearchParams(body).toString() : null;
      } else {
        console.log("Inside Else", body);
        requestBody = body;
      }

      response = await fetch(url, {
        method,
        headers,
        body: requestBody,
      });
    }

    if (!response || !response.ok) {
      const error = response ? await response.json().catch(() => null) : null;
      const errorMessage = error?.error || error?.message || 'Request failed';
      throw new Error(errorMessage);
    }
    switch (responseType) {
      case "blob":
        return await response.blob();
      case "text":
        return await response.text();
      case "none":
        return response;
      default:
        return await response.json();
    }
  }

  private async makeFileRequest(
    method: string,
    path: string,
    arrayBuffer: any = null,
    contentType: string = "image/jpeg"
  ): Promise<any> {
    const url = `${this.host}${path}`;
    const urlObject = new URL(url);
    const headers: any = {
      Host: urlObject.host,
      "Content-Type": contentType,
    };
    const authToken = this.getCookie("id_token");
    if (authToken) {
      headers["Authorization"] = `Bearer ${authToken}`;
    }
    let response: Response | null = null;

    //response = await this.aws.fetch(url, {
    response = await fetch(url, {
      method,
      headers,
      body: arrayBuffer,
    });
    if (!response || !response.ok) {
      const error = response ? await response.json().catch(() => null) : null;
      const errorMessage = error?.error || error?.message || 'Request failed';
      throw new Error(errorMessage);
    }
    return await response.json();
  }

  private async tryRefreshAndRetry(
    method: string,
    path: string,
    body: any,
    responseType: ResponseType,
    contentType: string
  ): Promise<any> {
    try {
      const refreshToken = Cookies.get("refresh_token");
      const sub = extractCognitoUsername();
      if (!refreshToken || !sub) return null;

      const tokens = await refreshCognitoTokens(this.host, refreshToken, sub);
      if (!tokens) return null;

      const retryUrl = `${this.host}${path}`;
      const retryHeaders: any = {
        Host: new URL(retryUrl).host,
        Authorization: `Bearer ${tokens.id_token}`,
      };
      if (contentType !== "multipart/form-data") {
        retryHeaders["Content-Type"] = contentType;
      }

      let retryBody: any = null;
      if (method === "POST") {
        if (contentType === "application/json") {
          retryBody = body ? JSON.stringify(body) : null;
        } else if (contentType === "application/x-www-form-urlencoded") {
          retryBody = body ? new URLSearchParams(body).toString() : null;
        } else {
          retryBody = body;
        }
      }

      const retryResp = await fetch(retryUrl, {
        method,
        headers: retryHeaders,
        ...(method !== "GET" && method !== "DELETE" ? { body: retryBody } : {}),
      });

      if (!retryResp.ok) return null;

      switch (responseType) {
        case "blob": return await retryResp.blob();
        case "text": return await retryResp.text();
        case "none": return retryResp;
        default: return await retryResp.json();
      }
    } catch {
      return null;
    }
  }

  public get(
    path: string,
    responseType: ResponseType = "json",
    contentType: string = "application/json"
  ): Promise<any> {
    return this.makeRequest("GET", path, null, responseType, contentType);
  }

  public delete(
    path: string,
    responseType: ResponseType = "json",
    contentType: string = "application/json"
  ): Promise<any> {
    return this.makeRequest("DELETE", path, null, responseType, contentType);
  }

  public post(
    path: string,
    body: any,
    responseType: ResponseType = "json",
    contentType: string = "application/json"
  ): Promise<any> {
    return this.makeRequest("POST", path, body, responseType, contentType);
  }

  public postExternal(
    path: string,
    body: any,
    responseType: ResponseType = "json",
    contentType: string = "application/json"
  ): Promise<any> {
    return this.makeExternalRequest(
      "POST",
      path,
      body,
      responseType,
      contentType
    );
  }

  public getExternal(
    path: string,
    responseType: ResponseType = "json",
    contentType: string = "application/json"
  ): Promise<any> {
    return this.makeExternalRequest(
      "GET",
      path,
      null,
      responseType,
      contentType
    );
  }

  public postFile(path: string, body: ArrayBuffer): Promise<any> {
    return this.makeFileRequest("POST", path, body);
  }
}

export default AwsApiClient;
